import { useState, useEffect, useCallback } from 'react';
import {
  Link2, Plus, Trash2, Video, User, Bell, Clock, Shield,
  ChevronRight, ChevronDown, AlertCircle, Loader, ToggleLeft,
  ToggleRight, DoorOpen, Phone, Activity, RefreshCw, Info
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import TargetPickerModal from '../components/TargetPickerModal';

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────

const SYSTEM_LABELS = {
  FEENICS:        'Feenics ACS',
  LENEL:          'Lenel OnGuard',
  CCURE:          'CCure 9000',
  BRIVO:          'Brivo',
  WAVE_VMS:       'Hanwha Wave',
  GENETEC_VMS:    'Genetec',
  MILESTONE_VMS:  'Milestone',
  MIRASYS_VMS:    'Mirasys',
  OOSTO:          'Oosto OnWatch',
  INTERCOM:       'Intercom',
  CCM_INTERNAL:   'CCM',
  RECORDING_ASSURANCE: 'Recording Assurance',
};

const PRIORITY_STYLES = {
  PRIMARY:    { label: 'Primary',    className: 'badge-green'  },
  SECONDARY:  { label: 'Secondary',  className: 'badge-blue'   },
  CONTEXTUAL: { label: 'Contextual', className: 'badge-gray'   },
};

const TARGET_ICONS = {
  CAMERA:             Video,
  USER:               User,
  WEBHOOK:            Link2,
  NOTIFICATION_GROUP: Bell,
  DOOR:               DoorOpen,
  INTERCOM:           Phone,
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function systemLabel(s) {
  return SYSTEM_LABELS[s] || s;
}

function truncateId(id, len = 16) {
  if (!id || id.length <= len) return id;
  return `${id.slice(0, 6)}…${id.slice(-6)}`;
}

function groupAssociationsBySource(associations) {
  const groups = {};
  for (const assoc of associations) {
    const key = `${assoc.sourceSystem}||${assoc.sourceDeviceId}`;
    if (!groups[key]) {
      groups[key] = {
        sourceSystem:     assoc.sourceSystem,
        sourceDeviceId:   assoc.sourceDeviceId,
        sourceDeviceType: assoc.sourceDeviceType,
        associations:     [],
      };
    }
    groups[key].associations.push(assoc);
  }
  return Object.values(groups).sort((a, b) =>
    a.sourceSystem.localeCompare(b.sourceSystem) || a.sourceDeviceId.localeCompare(b.sourceDeviceId)
  );
}

// ─────────────────────────────────────────────
// ASSOCIATION CARD (right-pane item)
// ─────────────────────────────────────────────

function AssociationCard({ assoc, onToggle, onDelete, onUpdate, saving }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ priority: assoc.priority, preRollSec: assoc.preRollSec, postRollSec: assoc.postRollSec });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const priorityInfo = PRIORITY_STYLES[assoc.priority] || PRIORITY_STYLES.SECONDARY;
  const TargetIcon = TARGET_ICONS[assoc.targetType] || Link2;

  const handleSaveEdit = async () => {
    await onUpdate(assoc.id, draft);
    setEditing(false);
  };

  return (
    <div className={`assoc-card ${!assoc.enabled ? 'disabled' : ''}`}>
      <div className="assoc-card-main">
        <div className="assoc-card-target">
          <TargetIcon size={16} className="assoc-card-icon" />
          <div className="assoc-card-info">
            <span className="assoc-card-name">
              {assoc.targetId.length > 24 ? truncateId(assoc.targetId) : assoc.targetId}
            </span>
            <span className="assoc-card-type">{assoc.targetType}</span>
          </div>
        </div>

        <div className="assoc-card-meta">
          <span className={`assoc-badge ${priorityInfo.className}`}>{priorityInfo.label}</span>
          <span className="assoc-roll-info">
            <Clock size={11} /> {assoc.preRollSec}s / {assoc.postRollSec}s
          </span>
        </div>

        <div className="assoc-card-actions">
          <button
            className="assoc-action-btn"
            title={assoc.enabled ? 'Disable' : 'Enable'}
            onClick={() => onToggle(assoc.id, !assoc.enabled)}
            disabled={saving}
          >
            {assoc.enabled
              ? <ToggleRight size={18} className="toggle-on" />
              : <ToggleLeft  size={18} className="toggle-off" />}
          </button>
          <button
            className="assoc-action-btn"
            title="Edit"
            onClick={() => { setEditing(!editing); setConfirmDelete(false); setDraft({ priority: assoc.priority, preRollSec: assoc.preRollSec, postRollSec: assoc.postRollSec }); }}
          >
            <Clock size={14} />
          </button>
          {!confirmDelete
            ? <button className="assoc-action-btn danger" title="Delete" onClick={() => setConfirmDelete(true)}>
                <Trash2 size={14} />
              </button>
            : <div className="assoc-confirm-delete">
                <span>Delete?</span>
                <button className="assoc-confirm-yes" onClick={() => onDelete(assoc.id)}>Yes</button>
                <button className="assoc-confirm-no"  onClick={() => setConfirmDelete(false)}>No</button>
              </div>
          }
        </div>
      </div>

      {editing && (
        <div className="assoc-card-edit">
          <div className="assoc-edit-row">
            <div className="assoc-edit-field">
              <label>Priority</label>
              <select
                value={draft.priority}
                onChange={e => setDraft(d => ({ ...d, priority: e.target.value }))}
              >
                <option value="PRIMARY">Primary</option>
                <option value="SECONDARY">Secondary</option>
                <option value="CONTEXTUAL">Contextual</option>
              </select>
            </div>
            <div className="assoc-edit-field">
              <label>Pre-roll (s)</label>
              <input
                type="number" min={0} max={300}
                value={draft.preRollSec}
                onChange={e => setDraft(d => ({ ...d, preRollSec: Number(e.target.value) }))}
              />
            </div>
            <div className="assoc-edit-field">
              <label>Post-roll (s)</label>
              <input
                type="number" min={0} max={300}
                value={draft.postRollSec}
                onChange={e => setDraft(d => ({ ...d, postRollSec: Number(e.target.value) }))}
              />
            </div>
          </div>
          <div className="assoc-edit-actions">
            <button className="btn-secondary sm" onClick={() => setEditing(false)}>Cancel</button>
            <button className="btn-primary sm" onClick={handleSaveEdit}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

export default function Associations() {
  const [associations, setAssociations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [expandedSystems, setExpandedSystems] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalPrefill, setModalPrefill] = useState(null);

  // ── Load associations ──
  const loadAssociations = useCallback(async () => {
    try {
      const r = await fetchWithTenant('/api/investigations/associations');
      if (!r.ok) throw new Error('Failed to load');
      const data = await r.json();
      setAssociations(data);
      setGroups(groupAssociationsBySource(data));
    } catch {
      toast.error('Failed to load associations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAssociations(); }, [loadAssociations]);

  // Group by sourceSystem for the left panel
  const systemGroups = groups.reduce((acc, g) => {
    const sys = g.sourceSystem;
    if (!acc[sys]) acc[sys] = [];
    acc[sys].push(g);
    return acc;
  }, {});

  // Associations for the selected source
  const selectedGroup = groups.find(g => `${g.sourceSystem}||${g.sourceDeviceId}` === selectedKey);
  const rightPaneAssociations = selectedGroup ? selectedGroup.associations : [];

  // ── Toggle enabled ──
  const handleToggle = async (id, enabled) => {
    setSaving(true);
    try {
      const r = await fetchWithTenant(`/api/investigations/associations/${id}/enabled`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!r.ok) throw new Error();
      setAssociations(prev => prev.map(a => a.id === id ? { ...a, enabled } : a));
      setGroups(prev => prev.map(g => ({
        ...g,
        associations: g.associations.map(a => a.id === id ? { ...a, enabled } : a)
      })));
      toast.success(enabled ? 'Association enabled' : 'Association disabled');
    } catch {
      toast.error('Failed to update association');
    } finally {
      setSaving(false);
    }
  };

  // ── Update (priority / roll) ──
  const handleUpdate = async (id, data) => {
    setSaving(true);
    try {
      const r = await fetchWithTenant(`/api/investigations/associations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error();
      const updated = await r.json();
      setAssociations(prev => prev.map(a => a.id === id ? updated : a));
      setGroups(prev => prev.map(g => ({
        ...g,
        associations: g.associations.map(a => a.id === id ? updated : a)
      })));
      toast.success('Association updated');
    } catch {
      toast.error('Failed to update association');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    setSaving(true);
    try {
      const r = await fetchWithTenant(`/api/investigations/associations/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error();
      const updated = associations.filter(a => a.id !== id);
      setAssociations(updated);
      const newGroups = groupAssociationsBySource(updated);
      setGroups(newGroups);
      // If selected group is now empty, deselect
      if (selectedGroup && selectedGroup.associations.length === 1) {
        setSelectedKey(null);
      }
      toast.success('Association deleted');
    } catch {
      toast.error('Failed to delete association');
    } finally {
      setSaving(false);
    }
  };

  // ── Create ──
  const handleCreate = async (payload) => {
    const r = await fetchWithTenant('/api/investigations/associations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to create');
    }
    const created = await r.json();
    const updated = [created, ...associations];
    setAssociations(updated);
    setGroups(groupAssociationsBySource(updated));
    // Select the new source
    setSelectedKey(`${created.sourceSystem}||${created.sourceDeviceId}`);
    // Expand its system group
    setExpandedSystems(prev => ({ ...prev, [created.sourceSystem]: true }));
    toast.success('Association created');
  };

  const openAddFromSource = (group) => {
    setModalPrefill({
      sourceSystem:     group.sourceSystem,
      sourceDeviceType: group.sourceDeviceType,
      sourceDeviceId:   group.sourceDeviceId,
    });
    setShowModal(true);
  };

  const toggleSystem = (sys) => {
    setExpandedSystems(prev => ({ ...prev, [sys]: !prev[sys] }));
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="assoc-page">
        <div className="assoc-loading">
          <Loader size={24} className="spin" />
          <span>Loading associations…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="assoc-page">
      {/* ── Page header — matches Admin Settings pattern ── */}
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title">
            <Link2 size={22} />
            Associations
          </h1>
          <p className="page-subtitle">Link source devices to targets — when a door opens, a camera gets bookmarked.</p>
        </div>
        <div className="page-header-actions">
          <button className="btn-icon" onClick={loadAssociations} title="Refresh" disabled={loading}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* ── Info banner (shown when no associations) ── */}
      {associations.length === 0 && (
        <div className="assoc-empty-banner">
          <Info size={16} />
          <div>
            <strong>No associations configured.</strong>
            <span> Create your first association to start correlating door access events with camera footage.</span>
          </div>
          <button className="btn-primary sm" onClick={() => setShowModal(true)}>
            <Plus size={14} /> Add Association
          </button>
        </div>
      )}

      {associations.length > 0 && (
        <div className="assoc-layout">
          {/* ── LEFT: Source panel ── */}
          <aside className="assoc-sources">
            <div className="assoc-sources-header">
              <span>Source Devices</span>
              <span className="assoc-sources-count">{groups.length}</span>
            </div>

            {Object.keys(systemGroups).length === 0 ? (
              <p className="assoc-sources-empty">No sources yet.</p>
            ) : (
              Object.entries(systemGroups).map(([sys, sysGroups]) => (
                <div key={sys} className="assoc-sys-group">
                  <button
                    className="assoc-sys-header"
                    onClick={() => toggleSystem(sys)}
                  >
                    {expandedSystems[sys] !== false
                      ? <ChevronDown size={14} />
                      : <ChevronRight size={14} />}
                    <span className="assoc-sys-name">{systemLabel(sys)}</span>
                    <span className="assoc-sys-badge">{sysGroups.length}</span>
                  </button>

                  {expandedSystems[sys] !== false && (
                    <div className="assoc-sys-items">
                      {sysGroups.map(g => {
                        const key = `${g.sourceSystem}||${g.sourceDeviceId}`;
                        const isSelected = key === selectedKey;
                        const TypeIcon = TARGET_ICONS[g.sourceDeviceType] || Link2;
                        return (
                          <button
                            key={key}
                            className={`assoc-source-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => setSelectedKey(isSelected ? null : key)}
                          >
                            <TypeIcon size={13} className="assoc-source-icon" />
                            <span className="assoc-source-id" title={g.sourceDeviceId}>
                              {truncateId(g.sourceDeviceId, 20)}
                            </span>
                            <span className="assoc-source-badge">{g.associations.length}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </aside>

          {/* ── RIGHT: Associations panel ── */}
          <main className="assoc-right">
            {!selectedKey ? (
              <div className="assoc-right-empty">
                <Link2 size={32} className="assoc-right-empty-icon" />
                <p>Select a source device to view its associations</p>
              </div>
            ) : (
              <>
                <div className="assoc-right-header">
                  <div className="assoc-right-title">
                    <span className="assoc-right-sys">{systemLabel(selectedGroup.sourceSystem)}</span>
                    <ChevronRight size={14} />
                    <span className="assoc-right-device" title={selectedGroup.sourceDeviceId}>
                      {truncateId(selectedGroup.sourceDeviceId, 28)}
                    </span>
                    <span className="assoc-right-type-badge">{selectedGroup.sourceDeviceType}</span>
                  </div>
                  <button
                    className="btn-secondary sm"
                    onClick={() => openAddFromSource(selectedGroup)}
                  >
                    <Plus size={13} /> Add Target
                  </button>
                </div>

                {rightPaneAssociations.length === 0 ? (
                  <div className="assoc-right-none">
                    <p>No associations for this source.</p>
                  </div>
                ) : (
                  <div className="assoc-cards">
                    {rightPaneAssociations.map(assoc => (
                      <AssociationCard
                        key={assoc.id}
                        assoc={assoc}
                        onToggle={handleToggle}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        saving={saving}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      )}

      <TargetPickerModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleCreate}
        prefill={modalPrefill}
      />
    </div>
  );
}
